import { Controller } from "@hotwired/stimulus"

// Sortable checkbox list where selected items float to top and can be reordered
export default class extends Controller {
  static targets = ["list", "item"]

  connect() {
    this.sortItems()
  }

  // Sort items: selected at top, unselected at bottom
  sortItems() {
    const list = this.listTarget
    const items = Array.from(this.itemTargets)

    const selected = items.filter(item => this.isSelected(item))
    const unselected = items.filter(item => !this.isSelected(item))

    // Re-append in order: selected first, then unselected
    selected.forEach(item => list.appendChild(item))
    unselected.forEach(item => list.appendChild(item))

    this.updateDragState()
  }

  // Called when checkbox changes
  toggle(event) {
    const item = event.target.closest("[data-sortable-attributes-target='item']")
    if (!item) return

    // Small delay to let checkbox state update
    requestAnimationFrame(() => {
      this.moveItem(item)
      this.updateDragState()
    })
  }

  // Move item to appropriate section
  moveItem(item) {
    const list = this.listTarget
    const isSelected = this.isSelected(item)

    if (isSelected) {
      // Move to bottom of selected section (before first unselected)
      const firstUnselected = this.itemTargets.find(i => !this.isSelected(i))
      if (firstUnselected) {
        list.insertBefore(item, firstUnselected)
      } else {
        list.appendChild(item)
      }
    } else {
      // Move to top of unselected section (after last selected)
      const lastSelected = [...this.itemTargets].reverse().find(i => this.isSelected(i))
      if (lastSelected) {
        list.insertBefore(item, lastSelected.nextSibling)
      } else {
        list.insertBefore(item, list.firstChild)
      }
    }
  }

  // Update which items can be dragged
  updateDragState() {
    this.itemTargets.forEach(item => {
      const handle = item.querySelector("[data-drag-handle]")
      const isSelected = this.isSelected(item)

      if (handle) {
        handle.classList.toggle("opacity-30", !isSelected)
        handle.classList.toggle("cursor-move", isSelected)
        handle.classList.toggle("cursor-not-allowed", !isSelected)
      }

      // Only selected items are draggable
      if (isSelected) {
        this.enableDrag(item)
      } else {
        this.disableDrag(item)
      }
    })
  }

  enableDrag(item) {
    const handle = item.querySelector("[data-drag-handle]")
    if (!handle || item._dragEnabled) return

    item._dragEnabled = true
    item._onMouseDown = () => item.draggable = true
    item._onMouseUp = () => item.draggable = false
    item._onDragStart = (e) => this.onDragStart(e, item)
    item._onDragOver = (e) => this.onDragOver(e, item)
    item._onDragEnd = (e) => this.onDragEnd(e, item)

    handle.addEventListener("mousedown", item._onMouseDown)
    handle.addEventListener("mouseup", item._onMouseUp)
    item.addEventListener("dragstart", item._onDragStart)
    item.addEventListener("dragover", item._onDragOver)
    item.addEventListener("dragend", item._onDragEnd)
  }

  disableDrag(item) {
    const handle = item.querySelector("[data-drag-handle]")
    if (!handle || !item._dragEnabled) return

    item._dragEnabled = false
    item.draggable = false

    handle.removeEventListener("mousedown", item._onMouseDown)
    handle.removeEventListener("mouseup", item._onMouseUp)
    item.removeEventListener("dragstart", item._onDragStart)
    item.removeEventListener("dragover", item._onDragOver)
    item.removeEventListener("dragend", item._onDragEnd)
  }

  onDragStart(e, item) {
    e.dataTransfer.effectAllowed = "move"
    this.draggedItem = item
    this.dragStartIndex = this.getSelectedIndex(item)
    setTimeout(() => item.classList.add("opacity-50"), 1)
  }

  onDragOver(e, item) {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"

    if (!this.draggedItem || this.draggedItem === item) return
    if (!this.isSelected(item)) return // Only reorder within selected items

    const list = this.listTarget
    const draggedIndex = this.getSelectedIndex(this.draggedItem)
    const targetIndex = this.getSelectedIndex(item)

    if (draggedIndex < targetIndex) {
      list.insertBefore(this.draggedItem, item.nextSibling)
    } else {
      list.insertBefore(this.draggedItem, item)
    }
  }

  onDragEnd(e, item) {
    item.draggable = false
    item.classList.remove("opacity-50")
    this.draggedItem = null
    this.dragStartIndex = null
  }

  getSelectedIndex(item) {
    const selected = this.itemTargets.filter(i => this.isSelected(i))
    return selected.indexOf(item)
  }

  isSelected(item) {
    const checkbox = item.querySelector("input[type='checkbox']")
    return checkbox?.checked && !checkbox?.disabled
  }
}
